import { AppType } from '~/core/constants/enum'
import { SETTINGS_CAREERS_URL } from '~/core/constants/url'

/**
 * Detect app type based on URL path
 */
export function detectAppType(pathname: string): AppType {
  if (pathname.includes('/careerhub/')) {
    return AppType.CHUB_APP
  }

  return AppType.INTERNAL_APP
}

export function isCareerHubApp(pathname: string): boolean {
  return pathname.includes('/careerhub/')
}

export function isCPageUrl(pathname: string): boolean {
  return pathname.includes('/careers/')
}

/** Public career apply pages: /careers/{slug} and custom-domain /career/{jobId} */
export function isPublicCareerPageUrl(pathname: string): boolean {
  return isCPageUrl(pathname) || pathname.includes('/career/')
}

/**
 * Extract locale from pathname
 */
export function getLocale(pathname: string): string {
  const match = pathname.match(/^\/(en|ja|vi)(\/|$)/)
  return match?.[1] || 'en'
}

export function decodeSlug(slug?: string): string {
  if (!slug) return ''
  try {
    let decoded = decodeURIComponent(slug)
    if (decoded !== slug && decoded.includes('%')) {
      decoded = decodeURIComponent(decoded)
    }
    return decoded
  } catch (error) {
    console.warn('Failed to decode slug:', slug, error)
    return slug
  }
}

/**
 * Extract tenant slug from CareerHub URL
 * @example /careerhub/{slug}/jobs, /careerhub/{slug}?returnUrl=...
 * @returns decoded slug or undefined
 */
export function getSlugTenantFromCareerHubUrl(path: string): string | undefined {
  // Chỉ dùng pathname, bỏ query string để tránh slug bị lẫn với query (gây redirect loop)
  const pathnameOnly = path.split('?')[0] ?? path
  const parts = pathnameOnly.split('/')
  const careerhubIndex = parts.indexOf('careerhub')
  if (careerhubIndex !== -1 && careerhubIndex + 1 < parts.length) {
    const slug = parts[careerhubIndex + 1]
    if (slug) {
      return decodeSlug(slug)
    }
  }
  return undefined
}

/**
 * Extract tenant slug from careers page URL
 * @example /careers/{slug}
 * @returns decoded slug or undefined
 */

export function getSlugFromCareersPage(path: string): string | undefined {
  const parts = path.split('/')
  const careersIndex = parts.indexOf('careers')
  if (careersIndex !== -1 && careersIndex + 1 < parts.length) {
    const slug = parts[careersIndex + 1]
    if (slug) {
      return decodeSlug(slug)
    }
  }
  return undefined
}

// Các route public không cần auth (login/register/error/... ở root)
const PUBLIC_UNAUTH_ROUTES = /^\/(login|register|verify-email|auth|400|404|500|access-denied)(\/|$)/

// Các route public của CareerHub theo domain động (login, register, error pages)
const PUBLIC_CHUB_ROUTES =
  /^\/careerhub\/[^/]+\/(login|register|verify-email|auth|400|404|500|access-denied)(\/|$)/

const regExpDynamicRoute = (resolvedUrl: string) => {
  const isAuthOrErrorPublicRoute =
    PUBLIC_UNAUTH_ROUTES.test(resolvedUrl) || PUBLIC_CHUB_ROUTES.test(resolvedUrl)

  // Route được xem là protected nếu:
  // - KHÔNG thuộc nhóm public auth/error ở trên
  // - HOẶC là SETTINGS_CAREERS_URL (route đặc biệt cần bảo vệ)
  return !isAuthOrErrorPublicRoute || [SETTINGS_CAREERS_URL].includes(resolvedUrl)
}

/**
 * Check if current page is protected route
 */
export const isProtectedRoute = (resolvedUrl: string) => {
  // Chỉ xét pathname, bỏ query/hash để tránh /login?returnUrl=... bị coi là protected → loop
  const pathnameOnly = (resolvedUrl.split('?')[0] ?? resolvedUrl).split('#')[0] ?? resolvedUrl
  const resolvedUrlWithoutLocale = pathnameOnly.replace(/\/(en|ja)\/?/, '/')

  // Remove leading slash for check exact match
  const pathWithoutSlash = resolvedUrlWithoutLocale.replace(/^\//, '').split('/')?.[0]

  // Exact match cho public routes
  const exactPublicRoutes = ['career', 'careers', 'welcome']
  if (exactPublicRoutes.includes(String(pathWithoutSlash))) {
    return false
  }

  return regExpDynamicRoute(resolvedUrlWithoutLocale)
}

/**
 * Get extracted path from returnUrl
 */
export function getPathFromReturnUrl(returnUrl: string) {
  try {
    return new URL(returnUrl).pathname
  } catch (e) {
    return returnUrl.split('?')[0]
  }
}

export function isSafeInternalReturnUrl(returnUrl?: string | null): returnUrl is string {
  return Boolean(returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//'))
}

export function shouldPreserveInternalReturnUrl(returnUrl?: string | null) {
  if (!isSafeInternalReturnUrl(returnUrl)) return false

  return returnUrl.startsWith('/ext') || returnUrl.startsWith('/download')
}
/**
 * Xác định route hiện tại có sử dụng locale từ URL hay không.
 * - Các trang public (login, register, careers, ...) dùng locale từ URL.
 * - Các trang internal dùng ngôn ngữ theo user setting.
 * - Bỏ locale prefix để `/ja/login` và `/login` được xử lý giống nhau.
 */
export function shouldUseLocaleFromUrl(pathname: string) {
  const pathnameWithoutLocale = pathname.replace(/^\/(en|ja|vi)(?=\/|$)/, '')
  const isCareerRoute = /\/careers(\/|$)/.test(pathnameWithoutLocale)
  return isCareerRoute || PUBLIC_UNAUTH_ROUTES.test(pathnameWithoutLocale)
}

export function getLocalePrefix(pathname: string): string | undefined {
  return pathname.match(/^\/(en|ja|vi)(\/|$)/)?.[1]
}

/**
 * Public unauth routes (login, 404, access-denied, ...) follow the URL locale
 * when a prefix is present. 403 redirects land on `/access-denied` with no
 * `/ja` prefix, so fall back to the signed-in user's language.
 */
export function resolveLayoutLocale({
  pathname,
  userLanguage,
  defaultLocale
}: {
  pathname: string
  userLanguage?: string | null
  defaultLocale: string
}) {
  const localeFromPath = getLocalePrefix(pathname)
  if (shouldUseLocaleFromUrl(pathname) && localeFromPath) {
    return localeFromPath
  }
  return userLanguage || localeFromPath || defaultLocale
}
